import React, { useContext, useEffect, useMemo } from 'react';
import { Form, Input, Modal, Select } from 'antd';

import GlobalContactsContext from '@totem/components/administration/globalContacts/GlobalContactsContext';
import { GlobalContactPayload } from '@totem/components/administration/globalContacts/types';

type FormValues = {
  name: string;
  title: string;
  email: string;
  phone: string;
  labels: string[];
};

const GlobalContactsFormModal = () => {
  const {
    modalOpen,
    closeModal,
    upsertContact,
    editingContact,
    saving,
    contacts,
  } = useContext(GlobalContactsContext);
  const [form] = Form.useForm<FormValues>();

  const existingLabels = useMemo(() => {
    const labelSet = new Set<string>();

    contacts.forEach((contact) => {
      contact.labels?.forEach((label) => labelSet.add(label));
    });

    return Array.from(labelSet).sort();
  }, [contacts]);

  useEffect(() => {
    if (modalOpen) {
      if (editingContact) {
        form.setFieldsValue({
          name: editingContact.name,
          title: editingContact.title,
          email: editingContact.email,
          phone: editingContact.phone,
          labels: editingContact.labels ?? [],
        });
      } else {
        form.resetFields();
      }
    }
  }, [modalOpen, editingContact, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: GlobalContactPayload = {
      id: editingContact?.id,
      name: values.name.trim(),
      title: values.title?.trim() ?? '',
      email: values.email?.trim() ?? '',
      phone: values.phone?.trim() ?? '',
      labels: values.labels ?? [],
    };

    await upsertContact(payload);
  };

  const title = editingContact ? 'Edit Contact' : 'Add Contact';

  return (
    <Modal
      open={modalOpen}
      onOk={handleSubmit}
      okText={editingContact ? 'Save Changes' : 'Add Contact'}
      confirmLoading={saving}
      onCancel={closeModal}
      title={title}
      destroyOnHidden
      maskClosable={false}
    >
      <Form layout="vertical" form={form} name="globalContactForm">
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: 'Please enter a name' },
            { max: 200, message: 'Name must be under 200 characters' },
            {
              validator: (_, value) =>
                value && value.trim().length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error('Name cannot be blank')),
            },
          ]}
        >
          <Input placeholder="Full name" />
        </Form.Item>
        <Form.Item
          label="Title"
          name="title"
          rules={[{ max: 200, message: 'Title must be under 200 characters' }]}
        >
          <Input placeholder="Job title or role" />
        </Form.Item>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { type: 'email', message: 'Please enter a valid email address' },
          ]}
        >
          <Input placeholder="email@example.com" />
        </Form.Item>
        <Form.Item
          label="Phone"
          name="phone"
          rules={[{ max: 50, message: 'Phone must be under 50 characters' }]}
        >
          <Input placeholder="Phone number" />
        </Form.Item>
        <Form.Item label="Labels" name="labels">
          <Select
            mode="tags"
            allowClear
            placeholder="Select or type to add labels"
            options={existingLabels.map((label) => ({
              label,
              value: label,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GlobalContactsFormModal;
